import { Plus, TriangleAlert, Trash2 } from 'lucide-react';
import type { GradeBand } from '@/store/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Field';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  ═════════════════════════════════════════════════════════════════════════════
  EDITING A SET OF GRADE BANDS

  One editor, two places: the school's scale (what new exams start from) and a
  single exam's own copy. They are the same table of numbers and the same
  rules, and two copies of it is how one of them ends up allowing a gap the
  other refuses.

  Why the upper bound is shown and not typed
  ──────────────────────────────────────────
  Each band is "this score and above", and its ceiling is simply the next band
  minus one. Asking for both ends invites overlapping or gap-ridden ranges, and
  then a child's mark falls into two grades or none.
  ═════════════════════════════════════════════════════════════════════════════
*/

/** Highest first, which is the order both the UI and `gradeFor` read them in. */
export const sortBands = (bands: GradeBand[]): GradeBand[] =>
  [...bands].sort((a, b) => b.minScore - a.minScore);

export function bandsAreUsable(bands: GradeBand[]): boolean {
  if (bands.length < 2) return false;
  if (!bands.every((b) => b.grade.trim() && b.remark.trim())) return false;
  /* A scale that does not reach zero leaves a hole somebody's child falls
     into — the API refuses it too, but saying so before Save is cheaper. */
  return sortBands(bands)[bands.length - 1]!.minScore === 0;
}

export function GradeBandsEditor({
  bands,
  onChange,
  canEdit = true,
  compact = false,
}: {
  bands: GradeBand[];
  onChange: (next: GradeBand[]) => void;
  canEdit?: boolean;
  /** Tighter rows, for when this sits inside a larger form. */
  compact?: boolean;
}) {
  const sorted = sortBands(bands);
  const reachesZero = sorted.length > 0 && sorted[sorted.length - 1]!.minScore === 0;

  const update = (band: GradeBand, patch: Partial<GradeBand>) =>
    onChange(bands.map((b) => (b === band ? { ...b, ...patch } : b)));

  const cols = 'grid-cols-[64px_72px_1fr_36px]';

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-clip rounded-md border border-border">
        <div className={cn('grid gap-2 border-b border-border bg-muted/40 px-3 py-1.5', cols)}>
          <span className={styles.label}>From</span>
          <span className={styles.label}>Grade</span>
          <span className={styles.label}>Remark</span>
          <span />
        </div>

        {sorted.map((band, i) => {
          const ceiling = i === 0 ? 100 : sorted[i - 1]!.minScore - 1;
          return (
            <div
              key={`${band.grade}-${band.minScore}-${i}`}
              className={cn(
                'grid items-center gap-2 border-b border-border px-3 last:border-b-0',
                cols,
                compact ? 'py-1' : 'py-1.5',
              )}
            >
              <Input
                aria-label={`Lowest score for grade ${band.grade}`}
                type="number"
                min={0}
                max={100}
                disabled={!canEdit}
                value={band.minScore}
                onChange={(e) =>
                  update(band, { minScore: Math.max(0, Math.min(100, Number(e.target.value))) })
                }
              />
              <Input
                aria-label={`Grade for ${band.minScore} and above`}
                maxLength={4}
                disabled={!canEdit}
                value={band.grade}
                onChange={(e) => update(band, { grade: e.target.value })}
              />
              <div className="flex min-w-0 items-center gap-2">
                <Input
                  aria-label={`Remark for grade ${band.grade}`}
                  className="min-w-0 flex-1"
                  maxLength={40}
                  disabled={!canEdit}
                  value={band.remark}
                  onChange={(e) => update(band, { remark: e.target.value })}
                />
                {/* Derived, never typed — see the note above. */}
                <span className="flex-shrink-0 text-[10.5px] tabular-nums text-muted-foreground">
                  to {ceiling}
                </span>
              </div>
              {canEdit && bands.length > 2 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={`Remove grade ${band.grade}`}
                  onClick={() => onChange(bands.filter((b) => b !== band))}
                >
                  <Trash2 size={13} />
                </Button>
              ) : (
                <span />
              )}
            </div>
          );
        })}
      </div>

      {canEdit && (
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onChange([...bands, { minScore: 0, grade: '', remark: '' }])}
          >
            <Plus size={13} className="mr-1" />
            Add a grade
          </Button>
          {!reachesZero && (
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-[hsl(var(--record-ink-warning))]">
              <TriangleAlert size={12} />
              The lowest band must start at 0, or some scores would have no grade.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
